import { zodResolver } from "@hookform/resolvers/zod"
import { useNavigate } from "@tanstack/react-router"
import { useCallback, useEffect, useRef, useState } from "react"
import { useForm } from "react-hook-form"

import type { IncomingPurchaseOrdersFormValues } from "../schemas"
import { incomingPurchaseOrdersFormSchema } from "../schemas"
import { getIncomingPurchaseOrdersFormDefaultValues, serializeIncomingPurchaseOrdersForm } from "../utils"

import { useUpdateGoodsReceiptLines } from "./useUpdateGoodsReceiptLines"

import type { GoodsReceipt, GoodsReceiptProductDetail } from "@/graphql/codegen/graphql"
import type { ActionOutcome } from "@/types/common"
import { runWithGuardRef } from "@/utils/forms"

interface UseIncomingPurchaseOrdersFormParams {
  goodsReceipt: GoodsReceipt | null
  onSuccess: () => void
}

interface UseIncomingPurchaseOrdersFormReturn {
  methods: ReturnType<typeof useForm<IncomingPurchaseOrdersFormValues>>
  canSubmit: boolean
  isCancelDialogOpen: boolean
  isResettingRef: React.MutableRefObject<boolean>
  handleCancel: () => void
  handleCloseCancelConfirmation: () => void
  handleConfirmCancel: () => void
  handleSave: () => Promise<ActionOutcome<GoodsReceiptProductDetail[]>>
  handleReset: (formValues: IncomingPurchaseOrdersFormValues) => void
}

export const EMPTY_DEFAULT_FORM_VALUES: IncomingPurchaseOrdersFormValues = { goodsReceiptId: "", purchaseOrders: [] }

export const useIncomingPurchaseOrdersForm = ({
  goodsReceipt,
  onSuccess,
}: UseIncomingPurchaseOrdersFormParams): UseIncomingPurchaseOrdersFormReturn => {
  const navigate = useNavigate()
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false)
  const isResettingRef = useRef(false)
  const methods = useForm<IncomingPurchaseOrdersFormValues>({
    defaultValues: EMPTY_DEFAULT_FORM_VALUES,
    resolver: zodResolver(incomingPurchaseOrdersFormSchema),
    mode: "onChange",
  })
  const {
    formState: { isDirty, isValid, isSubmitting },
    getValues,
    reset,
    trigger,
  } = methods
  const { update } = useUpdateGoodsReceiptLines(onSuccess)
  const canSubmit = isDirty && isValid && !isSubmitting

  const handleReset = useCallback(
    (formValues: IncomingPurchaseOrdersFormValues) => {
      runWithGuardRef(() => reset(formValues), isResettingRef)
    },
    [reset]
  )

  useEffect(() => {
    if (goodsReceipt) {
      const formValues = getIncomingPurchaseOrdersFormDefaultValues(goodsReceipt)
      handleReset(formValues)
    }
  }, [goodsReceipt, handleReset])

  const handleCancel = () => {
    if (isDirty) {
      setIsCancelDialogOpen(true)
    } else {
      navigate({ to: "/app/warehouse-receiving/incoming" })
    }
  }

  const handleCloseCancelConfirmation = () => setIsCancelDialogOpen(false)

  const handleConfirmCancel = () => {
    handleReset(EMPTY_DEFAULT_FORM_VALUES)
    navigate({ to: "/app/warehouse-receiving/incoming" })
  }

  const handleSave = useCallback(async (): Promise<ActionOutcome<GoodsReceiptProductDetail[]>> => {
    const isValid = await trigger()

    if (!isValid) {
      return { error: new Error("Please correct all validation errors before saving.") }
    }

    const formValues = getValues()
    const input = serializeIncomingPurchaseOrdersForm(formValues)

    return await update(input)
  }, [trigger, getValues, update])

  return {
    methods,
    canSubmit,
    isCancelDialogOpen,
    isResettingRef,
    handleCloseCancelConfirmation,
    handleConfirmCancel,
    handleCancel,
    handleSave,
    handleReset,
  }
}
